import { getMeContract } from './auth/definitions/get-me.contract.js';
import { loginUserContract } from './auth/definitions/login-user.contract.js';
import { refreshTokenContract } from './auth/definitions/refresh-token.contract.js';
import { getCoverageMatrixContract } from './benchmarks/definitions/get-coverage-matrix.contract.js';
import { getDashboardStatsContract } from './benchmarks/definitions/get-dashboard-stats.contract.js';
import { compareVehiclesContract } from './compare/definitions/compare-vehicles.contract.js';
import { getCommonScenariosContract } from './compare/definitions/get-common-scenarios.contract.js';
import { listCompatibilityContract } from './config/definitions/list-compatibility.contract.js';
import { listValidityRulesContract } from './config/definitions/list-validity-rules.contract.js';
import { updateCompatibilityContract } from './config/definitions/update-compatibility.contract.js';
import { updateValidityRuleContract } from './config/definitions/update-validity-rule.contract.js';
import { getHealthContract } from './health/definitions/get-health.contract.js';
import { commitImportContract } from './import/definitions/commit-import.contract.js';
import { downloadImportTemplateContract } from './import/definitions/download-import-template.contract.js';
import { getImportBatchContract } from './import/definitions/get-import-batch.contract.js';
import { listImportBatchesContract } from './import/definitions/list-import-batches.contract.js';
import { previewImportContract } from './import/definitions/preview-import.contract.js';
import { listProtocolsContract } from './protocols/definitions/list-protocols.contract.js';
import { generateReportContract } from './reports/definitions/generate-report.contract.js';
import { getReportContract } from './reports/definitions/get-report.contract.js';
import { listReportsContract } from './reports/definitions/list-reports.contract.js';
import { saveReportContract } from './reports/definitions/save-report.contract.js';
import { createRunDraftContract } from './runs/definitions/create-run-draft.contract.js';
import { getRunContract } from './runs/definitions/get-run.contract.js';
import { invalidateRunContract } from './runs/definitions/invalidate-run.contract.js';
import { listRunsContract } from './runs/definitions/list-runs.contract.js';
import { revalidateRunContract } from './runs/definitions/revalidate-run.contract.js';
import { submitRunContract } from './runs/definitions/submit-run.contract.js';
import { updateRunConditionsContract } from './runs/definitions/update-run-conditions.contract.js';
import { updateRunMeasurementsContract } from './runs/definitions/update-run-measurements.contract.js';
import { createRouteContract } from './routes/definitions/create-route.contract.js';
import { listRoutesContract } from './routes/definitions/list-routes.contract.js';
import { newRouteVersionContract } from './routes/definitions/new-route-version.contract.js';
import { updateRouteContract } from './routes/definitions/update-route.contract.js';
import { createScenarioContract } from './scenarios/definitions/create-scenario.contract.js';
import { getScenarioContract } from './scenarios/definitions/get-scenario.contract.js';
import { listScenariosContract } from './scenarios/definitions/list-scenarios.contract.js';
import { toggleScenarioStatusContract } from './scenarios/definitions/toggle-scenario-status.contract.js';
import { updateScenarioContract } from './scenarios/definitions/update-scenario.contract.js';
import { createSegmentContract } from './segments/definitions/create-segment.contract.js';
import { listSegmentsContract } from './segments/definitions/list-segments.contract.js';
import { updateSegmentContract } from './segments/definitions/update-segment.contract.js';
import { archiveVehicleContract } from './vehicles/definitions/archive-vehicle.contract.js';
import { createPublishedResultContract } from './vehicles/definitions/create-published-result.contract.js';
import { createVehicleContract } from './vehicles/definitions/create-vehicle.contract.js';
import { deletePublishedResultContract } from './vehicles/definitions/delete-published-result.contract.js';
import { getVehicleAttributeOptionsContract } from './vehicles/definitions/get-vehicle-attribute-options.contract.js';
import { getVehicleContract } from './vehicles/definitions/get-vehicle.contract.js';
import { listVehiclesContract } from './vehicles/definitions/list-vehicles.contract.js';
import { updateVehicleContract } from './vehicles/definitions/update-vehicle.contract.js';
// Every zod schema, in one place, so `fuel-benchmark-contracts/schemas` and this entry point can never drift.
export * from './schemas.js';
export * from './auth/definitions/index.js';
export * from './health/definitions/index.js';
export * from './segments/definitions/index.js';
export * from './vehicles/definitions/index.js';
export * from './protocols/definitions/index.js';
export * from './scenarios/definitions/index.js';
export * from './routes/definitions/index.js';
export * from './runs/definitions/index.js';
export * from './import/definitions/index.js';
export * from './config/definitions/index.js';
export * from './compare/definitions/index.js';
export * from './benchmarks/definitions/index.js';
export * from './reports/definitions/index.js';
/**
 * The single aggregate contract. The service implements it with `@Implement(fuelBenchmarkContract.<area>.<op>)`
 * and the web app consumes it through `OpenAPILink(fuelBenchmarkContract, …)`.
 */
export const fuelBenchmarkContract = {
    auth: {
        login: loginUserContract,
        refresh: refreshTokenContract,
        me: getMeContract,
    },
    health: {
        get: getHealthContract,
    },
    segments: {
        list: listSegmentsContract,
        create: createSegmentContract,
        update: updateSegmentContract,
    },
    vehicles: {
        // Declared before `get` so the static `/vehicles/attribute-options` path is matched first.
        attributeOptions: getVehicleAttributeOptionsContract,
        list: listVehiclesContract,
        get: getVehicleContract,
        create: createVehicleContract,
        update: updateVehicleContract,
        archive: archiveVehicleContract,
        publishedResults: {
            create: createPublishedResultContract,
            delete: deletePublishedResultContract,
        },
    },
    protocols: {
        list: listProtocolsContract,
    },
    scenarios: {
        list: listScenariosContract,
        get: getScenarioContract,
        create: createScenarioContract,
        update: updateScenarioContract,
        toggleStatus: toggleScenarioStatusContract,
    },
    routes: {
        list: listRoutesContract,
        create: createRouteContract,
        newVersion: newRouteVersionContract,
        update: updateRouteContract,
    },
    runs: {
        list: listRunsContract,
        get: getRunContract,
        createDraft: createRunDraftContract,
        updateConditions: updateRunConditionsContract,
        updateMeasurements: updateRunMeasurementsContract,
        submit: submitRunContract,
        revalidate: revalidateRunContract,
        invalidate: invalidateRunContract,
    },
    import: {
        // `/import/template` and `/import/batches` are declared before `/import/batches/{id}` so the static
        // paths are matched first, exactly as `vehicles.attributeOptions` is.
        downloadTemplate: downloadImportTemplateContract,
        preview: previewImportContract,
        commit: commitImportContract,
        listBatches: listImportBatchesContract,
        getBatch: getImportBatchContract,
    },
    config: {
        validityRules: {
            list: listValidityRulesContract,
            update: updateValidityRuleContract,
        },
        compatibility: {
            list: listCompatibilityContract,
            update: updateCompatibilityContract,
        },
    },
    compare: {
        // `/compare/common-scenarios` is declared first so the longer static path is matched before `/compare`.
        commonScenarios: getCommonScenariosContract,
        compare: compareVehiclesContract,
    },
    benchmarks: {
        // Declared before the param sibling so `/benchmarks/dashboard-stats` is matched as a static path.
        dashboardStats: getDashboardStatsContract,
        coverageMatrix: getCoverageMatrixContract,
    },
    reports: {
        // `/reports/generate` is declared before `/reports/{id}` so the static path is matched first, exactly
        // as `vehicles.attributeOptions` and `import.downloadTemplate` are.
        generate: generateReportContract,
        save: saveReportContract,
        list: listReportsContract,
        get: getReportContract,
        // No update and no delete on purpose: a saved report is an immutable snapshot (plan §3.3, §8).
    },
};
//# sourceMappingURL=index.js.map